import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  DescriptionList,
  ErrorState,
  LoadingState,
  Page,
  Text,
} from "@marekl/adminer/components";
import { AppTopbar } from "../../../shell/AppTopbar";
import { DisciplineFormModal } from "../../../components/DisciplineFormModal";
import { RuleChecks } from "../../../components/RuleChecks";
import { floorLabel, useRuleSet } from "../../../lib/queries/rules";
import { checkDiscipline } from "../../../lib/rulesChecks";
import {
  MUSIC_SOURCE_LABELS,
  RESULT_FORMULA_LABELS,
  formatTimeRange,
} from "../../../lib/rulesLabels";
import { CategoryMatrix } from "./CategoryMatrix";
import { readsRuleSetsOnly, useMe } from "../../../auth/queries";
import { ROLE_LABEL } from "../../../lib/i18n/roles";
import { DisciplineRecordingCard } from "./DisciplineRecordingCard";

export function DisciplineDetailPage() {
  const { data: me } = useMe();
  // Čtení soutěží: the discipline shown, as read-only as an archived set.
  const viewOnly = readsRuleSetsOnly(me);
  const { id, disciplineId } = useParams<{ id: string; disciplineId: string }>();
  const ruleSetId = id ?? "";
  const [editing, setEditing] = useState(false);

  const { data, isLoading, error, refetch } = useRuleSet(ruleSetId);
  const discipline = data?.disciplines.find((d) => d.id === disciplineId);

  const crumbs = [
    { label: "Sady pravidel", href: "/admin/pravidla" },
    ...(data ? [{ label: data.ruleset.name, href: `/admin/pravidla/${ruleSetId}` }] : []),
    { label: discipline?.name ?? "Disciplína" },
  ];

  if (isLoading) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState
              title="Sadu pravidel se nepodařilo načíst"
              error={error}
              onRetry={refetch}
            />
          </Page.Content>
        </Page>
      </>
    );
  }

  if (!discipline) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState title="Disciplína v této sadě pravidel není" />
          </Page.Content>
        </Page>
      </>
    );
  }

  const archived = data.ruleset.status === "archived";
  const readOnly = archived || viewOnly;
  // See RuleSetDetailPage: the axis tables freeze once a competition on
  // this set is published, while every value on the discipline stays
  // editable.
  const structureLocked = data.in_service && !readOnly;
  const ageTable = data.age_tables.find((t) => t.id === discipline.age_category_table_id);
  const formationTable = data.formation_tables.find(
    (t) => t.id === discipline.formation_table_id,
  );
  const classTable = data.performance_class_tables.find(
    (t) => t.id === discipline.performance_class_table_id,
  );
  const defaultFloor = data.floors.find((f) => f.id === discipline.default_floor_id);
  const combos = discipline.categories ?? [];
  const enabled = combos.filter((c) => c.enabled).length;
  const recordingIds = new Set(data.recordings.map((r) => r.id));

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header
          title={discipline.name}
          subtitle={`${discipline.family} · ${discipline.code}`}
          actions={
            readOnly ? undefined : (
              <Button variant="primary" onClick={() => setEditing(true)}>
                Upravit disciplínu
              </Button>
            )
          }
        />
        <Page.Content stack gap="lg">
          {viewOnly && (
            <Alert variant="info" title="Pouze pro čtení">
              Máte roli {ROLE_LABEL.competitions_viewer}: disciplínu vidíte celou, ale nic v ní neměníte.
            </Alert>
          )}
          {archived && !viewOnly && (
            <Alert variant="warn">
              Sada je archivovaná a jen pro čtení. Kombinace v ní už nelze měnit.
            </Alert>
          )}

          <Card>
            <Card.Header title="Výchozí nastavení" />
            <Card.Content>
              <DescriptionList>
                <DescriptionList.Item label="Časový limit">
                  <Text variant="mono">
                    {formatTimeRange(
                      discipline.default_time_min_seconds ?? null,
                      discipline.default_time_max_seconds ?? null,
                    )}
                  </Text>
                </DescriptionList.Item>
                <DescriptionList.Item label="Soutěžní plocha">
                  {defaultFloor ? (
                    floorLabel(defaultFloor)
                  ) : (
                    <Text variant="muted">nenastaveno</Text>
                  )}
                </DescriptionList.Item>
                <DescriptionList.Item label="Náčiní">
                  {discipline.apparatus || <Text variant="muted">nenastaveno</Text>}
                </DescriptionList.Item>
                <DescriptionList.Item label="Porota">
                  plusoví: {discipline.default_plus_judges} · techničtí: {discipline.default_tech_judges}
                  {" · "}
                  {RESULT_FORMULA_LABELS[discipline.default_result_formula]}
                </DescriptionList.Item>
                <DescriptionList.Item label="Hudba">
                  {MUSIC_SOURCE_LABELS[discipline.music_source]}
                </DescriptionList.Item>
                <DescriptionList.Item label="Srážková tabulka">
                  {data.deduction_tables.find(
                    (t) => t.id === discipline.default_deduction_table_id,
                  )?.name ?? <Text variant="muted">nenastaveno</Text>}
                </DescriptionList.Item>
                <DescriptionList.Item label="Hodnoticí tabulka">
                  {data.scoring_sheets.find((s) => s.id === discipline.default_scoring_sheet_id)
                    ?.name ?? <Text variant="muted">nenastaveno</Text>}
                </DescriptionList.Item>
                <DescriptionList.Item label="Osy kombinací">
                  {ageTable?.name ?? "—"} × {formationTable?.name ?? "—"} ×{" "}
                  {classTable?.name ?? "—"}
                </DescriptionList.Item>
                {discipline.restrictions && (
                  <DescriptionList.Item label="Omezení">
                    {discipline.restrictions}
                  </DescriptionList.Item>
                )}
              </DescriptionList>
            </Card.Content>
          </Card>

          {discipline.music_source === "federation" && (
            <DisciplineRecordingCard
              // Keyed: this is a param route, so navigating between two
              // federation disciplines reuses the element and the card's
              // state would still describe the previous one.
              key={discipline.id}
              discipline={discipline}
              recordings={data.recordings}
            />
          )}

          <Page.SectionHeader
            title="Kombinace"
            meta={
              <Text variant="muted">
                soutěží se {enabled} z {combos.length}
              </Text>
            }
          />

          <RuleChecks warnings={checkDiscipline(discipline, recordingIds)} />

          <CategoryMatrix
            discipline={discipline}
            readOnly={readOnly}
            ageCategories={ageTable?.categories ?? []}
            formationSizes={formationTable?.sizes ?? []}
            performanceClasses={classTable?.classes ?? []}
            deductionTables={data.deduction_tables}
            scoringSheets={data.scoring_sheets}
            floors={data.floors}
            recordings={data.recordings}
          />
        </Page.Content>
      </Page>

      {editing && (
        <DisciplineFormModal
          key={discipline.id}
          open
          workspace={data}
          discipline={discipline}
          structureLocked={structureLocked}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}
